import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, InputGroup } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';
import FormInput from '../../components/FormInput/FormInput';
import Spinner from '../../components/Spinner/Spinner';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useActivityPlansStore } from '../../store/providers/ActivityPlans';

import OptionsHelper from '../../lib/OptionsHelper';
import { getListState, saveListState } from '../../lib/ListStateHelper';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { columns, sorting } from './ActivityPlansSchema';
import * as constants from '../../constants';
import Permission from '../../components/Permission/Permission';

const DocumentSetupList = React.lazy(() => import('../../components/Lists/DocumentSetupList/DocumentSetupList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function ActivityPlans(props) {
    const dispatch = useDispatch();

    const listKey = '/activityPlans';
    const savedListState = getListState(listKey) || {};

    const pagePermission = constants.PERMISSION_CONTENT_ACTIVITYPLANS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData, listCategories } = useActivityPlansStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState(savedListState.categoryId || '');
    const [listData, setListData] = useState([]);
    const [listCategoryData, setListCategoryData] = useState([]);
    const [listCategoryOptions, setListCategoryOptions] = useState([]);

    const selectedHandler = (activityPlan) => {
        navigate(`/activityPlan/edit/${activityPlan.id}`);
    };

    const addedHandler = () => {
        navigate(`/activityPlan/new`);
    };

    const deletedHandler = (activityPlan) => {
        deleteRecord({ activityPlanId: activityPlan.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const moreFilters = (listCategoryOptions) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Plan Category:</InputGroup.Addon>
                        <FormInput id="categoryId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedCategory}
                        >
                            {listCategoryOptions}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    const changedHandler = (id, value) => {
        if (id === 'categoryId') {
            const categoryId = value ? parseInt(value) : '';
            setSelectedCategory(categoryId);
            saveListState(listKey, { categoryId });
            listRecords(categoryId ? { categoryId } : {});
        }
    }

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                let globalPlans = [];
                data.body.activityPlans.forEach((plan) => {
                    if (plan.franchiseId == null) {
                        globalPlans.push(plan);
                    }
                });
                setListData(globalPlans);
                setIsLoaded(true);
            }

            if (data.status === 'success' && data.scope === 'listcategories') {
                setListCategoryData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords, listCategories]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'activityPlans', text: 'Activity Plans', path: window.location.pathname }));
        listRecords(selectedCategory ? { categoryId: selectedCategory } : {});
        listCategories();

        setIsMounted(true);
    }, [hasPermission, isMounted, listRecords, dispatch, listCategories]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(listCategoryData)) {
            const categoryOptions = listCategoryData && listCategoryData.map(category => {
                return {
                    id: category.id,
                    name: category.name,
                    description: ''
                }
            });
            setListCategoryOptions(OptionsHelper({ list: categoryOptions, defaultOptions: [['', 'All']], includeBlank: false }));
        }
    }, [listCategoryData]);

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isLoaded &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Activity Plans" />
                            <Panel>
                                <Panel.Body>
                                    <DocumentSetupList
                                        listKey={listKey}
                                        compType="Activity Plan"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        moreFilters={moreFilters(listCategoryOptions)}
                                        added={() => addedHandler()}
                                        selected={(activityPlan) => selectedHandler(activityPlan)}
                                        deleted={(activityPlan) => deletedHandler(activityPlan)}
                                        refreshed={() => refreshedHandler()}
                                        permissionEdit={canEdit}
                                    />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(ActivityPlans));